import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';

import { UnauthorizedError } from 'src/utils/errors/UnauthorizedError';

export const isUnauthorizedError = (error) => {
	if (!error) {
		return false;
	}

	if (typeof Response !== 'undefined' && error instanceof Response) {
		return error.status === 401;
	}

	if (error instanceof UnauthorizedError) {
		return true;
	}

	if (ServerError.is(error)) {
		return error.statusCode === 401 || error.response?.status === 401;
	}

	if (CombinedGraphQLErrors.is(error)) {
		const hasUnauthorizedGraphqlError = error.errors?.some(
			(item) =>
				item.extensions?.code === 'UNAUTHENTICATED' ||
				item.extensions?.status === 401 ||
				item.extensions?.http?.status === 401 ||
				isUnauthorizedError(item),
		);

		if (hasUnauthorizedGraphqlError) {
			return true;
		}
	}

	if (error.status === 401) {
		return true;
	}

	const statusCode =
		error.statusCode ??
		error.response?.status ??
		error.networkError?.statusCode ??
		error.cause?.statusCode;

	if (statusCode === 401) {
		return true;
	}

	const message = String(error.message ?? error);

	if (
		message.includes('status code 401') ||
		message.includes('Received status code 401') ||
		message.includes('Authentication required') ||
		message.includes('Authenticated user is missing') ||
		message.includes('Authenticated user is invalid') ||
		message.includes('UNAUTHENTICATED')
	) {
		return true;
	}

	if (Array.isArray(error.graphQLErrors)) {
		return error.graphQLErrors.some((item) => isUnauthorizedError(item));
	}

	if (error.networkError) {
		return isUnauthorizedError(error.networkError);
	}

	if (error.cause) {
		return isUnauthorizedError(error.cause);
	}

	if (Array.isArray(error.errors)) {
		return error.errors.some((item) => isUnauthorizedError(item));
	}

	return false;
};

export const toUnauthorizedError = (error) => {
	if (error instanceof UnauthorizedError) {
		return error;
	}

	if (isUnauthorizedError(error)) {
		return new UnauthorizedError(error);
	}

	return null;
};
